import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square } from 'lucide-react';
import { toast } from 'sonner';

function getSpeechRecognitionCtor() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function normalizeTranscript(text) {
  return String(text || '').replace(/\s+/g, ' ').trim();
}

export default function JollyDictationButton({
  onAppendText,
  disabled = false,
  label = 'Jolly',
  compact = false,
  title = 'Use Jolly dictation',
  languageHint = 'en-IN',
}) {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [interimText, setInterimText] = useState('');
  const recognitionRef = useRef(null);
  const manualStopRef = useRef(false);
  const heardSpeechRef = useRef(false);

  useEffect(() => {
    setSupported(Boolean(getSpeechRecognitionCtor()));
    return () => {
      try {
        recognitionRef.current?.stop();
      } catch {}
    };
  }, []);

  const stopListening = () => {
    manualStopRef.current = true;
    try {
      recognitionRef.current?.stop();
    } catch {}
    setListening(false);
    setInterimText('');
  };

  const startListening = () => {
    const SpeechRecognitionCtor = getSpeechRecognitionCtor();
    if (!SpeechRecognitionCtor) {
      toast.error('Jolly dictation is not available in this browser.');
      return;
    }
    try {
      const recognition = new SpeechRecognitionCtor();
      recognition.lang = String(languageHint || '').trim() || (navigator.language || 'en-IN');
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      manualStopRef.current = false;
      heardSpeechRef.current = false;

      recognition.onresult = event => {
        let finalTranscript = '';
        let nextInterim = '';
        for (let index = event.resultIndex; index < event.results.length; index += 1) {
          const transcript = normalizeTranscript(event.results[index][0]?.transcript);
          if (!transcript) continue;
          if (event.results[index].isFinal) {
            finalTranscript += `${transcript} `;
          } else {
            nextInterim += `${transcript} `;
          }
        }
        const cleanedFinal = normalizeTranscript(finalTranscript);
        const cleanedInterim = normalizeTranscript(nextInterim);
        setInterimText(cleanedInterim);
        if (cleanedFinal) {
          heardSpeechRef.current = true;
          onAppendText?.(cleanedFinal);
        }
      };

      recognition.onerror = event => {
        if (event?.error === 'no-speech') return;
        setListening(false);
        setInterimText('');
        toast.error('Jolly could not capture dictation right now.');
      };

      recognition.onend = () => {
        recognitionRef.current = null;
        setListening(false);
        setInterimText('');
        if (!manualStopRef.current && !heardSpeechRef.current) {
          toast.message('Jolly did not catch any speech.');
        }
      };

      recognitionRef.current = recognition;
      recognition.start();
      setListening(true);
    } catch {
      toast.error('Jolly dictation could not start.');
    }
  };

  const handleClick = () => {
    if (disabled) return;
    if (listening) {
      stopListening();
      return;
    }
    startListening();
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: compact ? '6px' : '8px', minWidth: 0 }}>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || !supported}
        title={!supported ? 'Jolly dictation is not supported in this browser' : listening ? 'Stop Jolly dictation' : `${title}${languageHint ? ` (${languageHint})` : ''}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          borderRadius: compact ? '10px' : '999px',
          border: `1px solid ${listening ? 'rgba(220, 38, 38, 0.24)' : 'var(--border-default)'}`,
          background: listening ? 'rgba(220, 38, 38, 0.08)' : 'var(--bg-elevated)',
          color: listening ? '#dc2626' : supported ? 'var(--text-secondary)' : 'var(--text-muted)',
          cursor: disabled || !supported ? 'not-allowed' : 'pointer',
          padding: compact ? '8px 10px' : '7px 11px',
          fontSize: '11px',
          fontWeight: 600,
          fontFamily: 'var(--font-ui)',
          opacity: disabled ? 0.65 : 1,
          flexShrink: 0,
        }}
      >
        {listening ? <Square size={12} fill="currentColor" /> : <Mic size={12} />}
        <span>{listening ? 'Listening' : label}</span>
      </button>
      {listening && interimText && (
        <span
          style={{
            minWidth: 0,
            fontSize: '11px',
            lineHeight: 1.5,
            color: 'var(--text-muted)',
            fontFamily: 'var(--font-ui)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {interimText}
        </span>
      )}
    </div>
  );
}
